/**
 * useGetListData hook
 */

// React
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

// Libraries
import { generatePath, useNavigate } from 'react-router-dom';
import { SetterOrUpdater, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

// Recoil
import { notifyAtom } from 'recoil/NotifyRecoil';
import { loadingAtom } from 'recoil/LoadingRecoil';
import { searchInputValueAtom } from 'recoil/ValueSearchRecoil';
import { paramsGetListAtom } from 'recoil/ParamsGetListRecoil';

// Utils
import { Pagination } from 'utils/types';
import { NotifyProps } from 'utils/types/atomTypes';
import { handleFileExport, handleGetListData, scrollToTop } from 'utils/helper/common';
import { ParamsGetListSetting, NOTIFICATION_MESSAGES, AWS_S3, StatusKey } from 'utils/constants';

// Router
import ROUTES from 'configs/route';

// Models
import { IParamGetList } from 'models/UserRequest';

// Type
type UseUserInfoReturn<T> = {
  perPage: number;
  totalPages: number;
  isLoading: boolean;
  currentPage: number;
  state: {
    id?: number | null;
    openEdit?: boolean;
    openRemove?: boolean;
    openInformation?: boolean;
  };
  totalRecords: number;
  typeSort: string | number;
  status?: string;
  dataList: T[] | undefined;
  paramsGetList: IParamGetList;
  stateInformation: T | undefined;
  serviceId: string | number | null;
  scrollContainerRef: React.RefObject<HTMLDivElement>;

  handleClose: () => void;
  isSortable: () => boolean;
  handleCloseInfo: () => void;
  handleCloseEdit: () => void;
  getListData: () => Promise<void>;
  handleEdit: (_id: number) => void;
  handleExport: () => Promise<void>;
  handleOpenInformation: () => void;
  getIndex: (_index: number) => number;
  setIsLoading: SetterOrUpdater<boolean>;
  setNotify: SetterOrUpdater<NotifyProps>;
  handleConfirmDelete: () => Promise<void>;
  navigateEditSystem: (_id: number) => void;
  handleRemove: (_id: number | null) => void;
  isIconVisible: (_status: string) => boolean;
  getIconUrlWithStatus: (_icon: any) => string;
  handleRequestSort: (_property: keyof T) => void;
  setParamsGetList: SetterOrUpdater<IParamGetList>;
  isDisabledEditSystem: (_services: any) => boolean;
  handleSelectTypeSort: (_event: ChangeEvent<HTMLInputElement>) => void;
  handleSelectStatus: (_event: ChangeEvent<HTMLInputElement>) => void;
  setStateInformation: React.Dispatch<React.SetStateAction<T | undefined>>;
  handleSelectByService: (_event: ChangeEvent<HTMLInputElement>) => void;
  handleChangePage: (_event: React.ChangeEvent<unknown>, _page: number) => void;
};

// Hook
const useGetListData = <T>(
  apiGetList?: (_params: IParamGetList) => Promise<any>,
  apiExport?: (_params: IParamGetList) => Promise<any>,
  apiDelete?: (_param: { id: number }) => Promise<any>,
  defaultParamsGetList?: IParamGetList,
  fileName?: string,
  titleError?: string
): UseUserInfoReturn<T> => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const [state, setState] = useState<{
    id?: number | null;
    openEdit?: boolean;
    openRemove?: boolean;
    openInformation?: boolean;
  }>({
    id: null,
    openEdit: false,
    openRemove: false,
    openInformation: false,
  });
  const [typeSort, setTypeSort] = useState<number | string>('all');
  const searchQuery = useRecoilValue(searchInputValueAtom);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [serviceId, setServiceId] = useState<number | string | null>(null);
  const rowsPerPage = ParamsGetListSetting.ROW_PER_PAGE;
  const [stateInformation, setStateInformation] = useState<T | undefined>(undefined);

  const [dataList, setDataList] = useState<T[]>();
  const [pagination, setPagination] = useState<Pagination>();
  const [paramsGetList, setParamsGetList] = useRecoilState(paramsGetListAtom);

  const navigate = useNavigate();
  const setNotify = useSetRecoilState(notifyAtom);
  const [isLoading, setIsLoading] = useRecoilState(loadingAtom);

  const totalRecords = useMemo(
    () => pagination?.total || dataList?.length || 0,
    [pagination, dataList]
  );

  const currentPage = useMemo(() => pagination?.current_page || 1, [pagination]);

  const perPage = useMemo(() => pagination?.per_page || rowsPerPage, [pagination]);

  const totalPages = useMemo(
    () => pagination?.last_page || (dataList ? Math.ceil(dataList.length / rowsPerPage) : 0),
    [pagination, dataList]
  );

  const scrollToStart = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  };

  const getListData = async () => {
    if (apiGetList) {
      const res = await handleGetListData({
        apiCall: () => apiGetList(paramsGetList),
        setIsLoading,
      });
      scrollToStart();
      scrollToTop();
      if (paramsGetList && Object.keys(paramsGetList).length > 0) {
        setDataList(res?.data ?? []);
        setPagination(res?.pagination ?? {});
      } else {
        setDataList(res ?? []);
      }
    }
  };

  const handleCloseInfo = () => {
    setState({ openInformation: false });
  };

  const handleCloseEdit = () => {
    setState({ id: null, openEdit: false });
    setStateInformation(undefined);
  };

  const handleSelectTypeSort = (event: ChangeEvent<HTMLInputElement>) => {
    const valueTypeSort = event.target.value;
    setTypeSort(valueTypeSort);
    setParamsGetList((prev) => ({
      ...prev,
      type: valueTypeSort,
    }));
  };

  const handleSelectStatus = (event: ChangeEvent<HTMLInputElement>) => {
    const valueStatusSort = event.target.value;
    setStatus(valueStatusSort);
    setParamsGetList((prev) => ({
      ...prev,
      keyword: searchQuery,
      status: valueStatusSort,
    }));
  };

  const handleSelectByService = (event: ChangeEvent<HTMLInputElement>) => {
    const valueTypeSort = event.target.value;
    setServiceId(valueTypeSort);
    setParamsGetList((prev) => ({
      ...prev,
      keyword: searchQuery,
      service_id: valueTypeSort,
    }));
  };

  const handleOpenInformation = () => {
    setState((prev) => ({ ...prev, openInformation: true }));
  };

  useEffect(() => {
    if (paramsGetList?.sort_name) {
      getListData();
    }
  }, [paramsGetList]);

  const handleChangePage = (event: unknown, newPage: number) => {
    if (currentPage !== newPage) {
      setParamsGetList((prev) => ({
        ...prev,
        page: newPage,
      }));
    }
  };

  const handleRequestSort = (property: keyof T) => {
    setParamsGetList((prev) => ({
      ...prev,
      sort_name: property as string,
      order_by: prev?.sort_name === property && prev?.order_by === 'desc' ? 'asc' : 'desc',
    }));
  };

  const isSortable = () => {
    return dataList?.length !== 0 && dataList?.length !== 1;
  };

  const handleRemove = (id: number | null) => {
    if (id) {
      setState({ id, openRemove: true, openInformation: false });
    }
  };

  const handleClose = () => {
    setState((prev) => ({ ...prev, openRemove: false }));
  };

  const handleEdit = (id: number) => {
    setState({ id: id, openEdit: true });
  };

  const handleConfirmDelete = async () => {
    try {
      if (state.id && apiDelete) {
        setIsLoading(true);
        handleClose();
        await apiDelete({ id: state.id });
        await getListData();
        setNotify({ open: true });
      }
    } catch (error) {
      setNotify({ open: true, severity: 'error', title: NOTIFICATION_MESSAGES.ERROR.DELETE });
      scrollToStart();
      scrollToTop();
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (apiExport && fileName && titleError)
      await handleFileExport({
        apiCall: () => apiExport(paramsGetList),
        fileName: fileName,
        setIsLoading,
        setNotify,
        titleError: titleError,
      });
  };

  const navigateEditSystem = (id: number) => {
    const path = generatePath(ROUTES.EDIT_SYSTEMS, { user_id: id });
    setNotify({ open: false });
    navigate(path);
  };

  const isIconVisible = (status: string) =>
    status !== StatusKey.NOT_USED && status !== StatusKey.CANCELED && status !== StatusKey.FINISHED;

  const getIndex = (index: number) => {
    return (currentPage - 1) * perPage + index + 1;
  };

  const getIconUrlWithStatus = (icon: any) => {
    return `${AWS_S3.BASE_URL}/${
      icon?.status === StatusKey.REQUESTED ? icon?.icon_status_deactive : icon?.icon_status_active
    }`;
  };

  const isDisabledEditSystem = (services: any) => {
    return services?.length === 0;
  };

  useEffect(() => {
    if (defaultParamsGetList) {
      setParamsGetList(defaultParamsGetList);
    }
  }, []);

  return {
    state,
    perPage,
    dataList,
    typeSort,
    status,
    isLoading,
    totalPages,
    currentPage,
    totalRecords,
    paramsGetList,
    serviceId,
    stateInformation,
    scrollContainerRef,

    getIndex,
    setNotify,
    isSortable,
    handleEdit,
    getListData,
    handleClose,
    setIsLoading,
    handleExport,
    handleRemove,
    isIconVisible,
    handleCloseEdit,
    handleCloseInfo,
    setParamsGetList,
    handleChangePage,
    handleRequestSort,
    navigateEditSystem,
    setStateInformation,
    handleConfirmDelete,
    handleSelectTypeSort,
    handleSelectStatus,
    getIconUrlWithStatus,
    isDisabledEditSystem,
    handleOpenInformation,
    handleSelectByService,
  };
};

export default useGetListData;
